import { useCallback } from 'react'

/**
 * Finds the focusable element for the given elementId using a single consolidated query
 * @param elementId - The ID of the element to find
 * @returns The focusable element or null if not found
 */
const findFocusableElement = (elementId: string): HTMLElement | null => {
    // Single query that checks all possible selectors in priority order
    const selectors = [
        `[data-testid="plain-text-${elementId}"]`,
        `[data-element-id="${elementId}"] [data-testid^="plain-text-"]`,
        `[data-element-id="${elementId}"] .ck-editor__editable`,
        `[data-element-id="${elementId}"] [contenteditable="true"]`
    ].join(', ')
    
    return document.querySelector(selectors) as HTMLElement || null
}

/**
 * Custom hook for restoring focus to an element after DOM updates.
 * Uses requestAnimationFrame to ensure React has completed rendering.
 * 
 * @returns A function to restore focus to an element by its ID
 */
export const useFocusRestoration = () => {
    /**
     * Restores focus to an element after promote/demote operations
     * @param elementId - The ID of the element to focus
     */
    const restoreFocus = useCallback((elementId: string) => {
        if (!elementId) return

        // Single RAF is sufficient for React render cycle completion
        requestAnimationFrame(() => {
            const focusableElement = findFocusableElement(elementId)
            
            if (focusableElement) {
                focusableElement.click()
                focusableElement.focus()
            }
        })
    }, [])

    return { restoreFocus }
}